import { create } from 'zustand';
import api from '../lib/api';
import toast from 'react-hot-toast';

const useTaskStore = create((set, get) => ({
  tasks: [],
  categories: [],
  isLoading: false,
  filters: {
    status: '',
    priority: '',
    category: '',
  },

  // Tasks
  fetchTasks: async (filters = {}) => {
    set({ isLoading: true });
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/tasks?${params}`);
      set({ tasks: response.data.tasks, isLoading: false });
      return { success: true };
    } catch (error) {
      set({ isLoading: false });
      toast.error('Failed to fetch tasks');
      return { success: false, error: error.response?.data?.error };
    }
  },

  createTask: async (taskData) => {
    try {
      const response = await api.post('/tasks', taskData);
      const newTask = response.data.task;
      
      set(state => ({
        tasks: [newTask, ...state.tasks]
      }));
      
      toast.success('Task created successfully');
      return { success: true, task: newTask };
    } catch (error) {
      toast.error('Failed to create task');
      return { success: false, error: error.response?.data?.error };
    }
  },

  updateTask: async (taskId, updates) => {
    try {
      const response = await api.put(`/tasks/${taskId}`, updates);
      const updatedTask = response.data.task;
      
      set(state => ({
        tasks: state.tasks.map(task => 
          task.id === taskId ? updatedTask : task
        )
      }));
      
      toast.success('Task updated successfully');
      return { success: true, task: updatedTask };
    } catch (error) {
      toast.error('Failed to update task');
      return { success: false, error: error.response?.data?.error };
    }
  },

  deleteTask: async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      
      set(state => ({
        tasks: state.tasks.filter(task => task.id !== taskId)
      }));
      
      toast.success('Task deleted successfully');
      return { success: true };
    } catch (error) {
      toast.error('Failed to delete task');
      return { success: false, error: error.response?.data?.error };
    }
  },

  toggleTaskStatus: async (taskId, currentStatus) => {
    const statusFlow = {
      'todo': 'in_progress',
      'in_progress': 'completed',
      'completed': 'todo'
    };
    
    const newStatus = statusFlow[currentStatus];
    return get().updateTask(taskId, { status: newStatus });
  },

  // Categories
  fetchCategories: async () => {
    try {
      const response = await api.get('/categories');
      set({ categories: response.data.categories });
      return { success: true };
    } catch (error) {
      toast.error('Failed to fetch categories');
      return { success: false, error: error.response?.data?.error };
    }
  },

  createCategory: async (categoryData) => {
    try {
      const response = await api.post('/categories', categoryData);
      const newCategory = response.data.category;
      
      set(state => ({
        categories: [...state.categories, newCategory]
      }));
      
      toast.success('Category created successfully');
      return { success: true, category: newCategory };
    } catch (error) {
      toast.error('Failed to create category');
      return { success: false, error: error.response?.data?.error };
    }
  },

  updateCategory: async (categoryId, updates) => {
    try {
      const response = await api.put(`/categories/${categoryId}`, updates);
      const updatedCategory = response.data.category;
      
      set(state => ({
        categories: state.categories.map(category => 
          category.id === categoryId ? updatedCategory : category
        )
      }));
      
      toast.success('Category updated successfully');
      return { success: true, category: updatedCategory };
    } catch (error) {
      toast.error('Failed to update category');
      return { success: false, error: error.response?.data?.error };
    }
  },

  deleteCategory: async (categoryId) => {
    try {
      await api.delete(`/categories/${categoryId}`);
      
      set(state => ({
        categories: state.categories.filter(category => category.id !== categoryId)
      }));
      
      toast.success('Category deleted successfully');
      return { success: true };
    } catch (error) {
      toast.error('Failed to delete category');
      return { success: false, error: error.response?.data?.error };
    }
  },

  // Filters
  setFilters: (filters) => {
    set(state => ({
      filters: { ...state.filters, ...filters }
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        status: '',
        priority: '',
        category: '',
      }
    });
  },
}));

export { useTaskStore };
